'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';

import type { GoogleReviewSummary } from '@/lib/google-review-summary';
import {
  googleReviewPlatform,
  googleReviews,
  secondaryReviewPlatforms,
} from '@/lib/review-platforms';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

function RatingStars() {
  return (
    <span className="flex items-center gap-1" aria-label="5 out of 5 stars">
      {Array.from({ length: 5 }).map((_, index) => (
        <svg
          key={index}
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-4 w-4 text-[#fbbc04]"
          aria-hidden="true"
        >
          <path d="M10 1.6l2.55 5.16 5.7.83-4.12 4.02.97 5.67L10 14.6l-5.1 2.68.97-5.67-4.12-4.02 5.7-.83L10 1.6z" />
        </svg>
      ))}
    </span>
  );
}

function GoogleMark() {
  return (
    <span className="inline-flex h-8 w-8 items-center justify-center border border-white/15 bg-white text-sm font-bold">
      <span className="text-[#4285f4]">G</span>
    </span>
  );
}

export default function GoogleReviewsSection({ summary }: { summary: GoogleReviewSummary }) {
  const hasMultipleReviews = googleReviews.length > 1;

  return (
    <section id="google-reviews" className="scroll-mt-32 bg-black py-16 text-white lg:py-24">
      <div className="container">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-400">
            Google reviews
          </p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-white md:text-4xl">
            What customers say about OKTshirt
          </h2>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <span className="inline-flex items-center gap-3 border border-white/15 bg-white/5 px-4 py-3">
              <GoogleMark />
              <span className="text-sm font-semibold text-white">Google</span>
              <RatingStars />
              <span className="text-sm text-gray-300">
                {summary.rating} from {summary.count} reviews
              </span>
            </span>
            <a
              href={googleReviewPlatform.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-white underline decoration-white/40 underline-offset-4 transition-colors hover:text-gray-300"
            >
              View all Google reviews
            </a>
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-4xl lg:mt-12">
          <Swiper
            modules={[Autoplay, Navigation, Pagination]}
            slidesPerView={1}
            spaceBetween={24}
            loop={hasMultipleReviews}
            navigation={hasMultipleReviews}
            pagination={
              hasMultipleReviews
                ? {
                    clickable: true,
                    bulletClass: 'okt-google-review-bullet',
                    bulletActiveClass: 'okt-google-review-bullet-active',
                  }
                : false
            }
            autoplay={
              hasMultipleReviews
                ? {
                    delay: 6500,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }
                : false
            }
            className="okt-google-reviews-swiper"
          >
            {googleReviews.map((review) => (
              <SwiperSlide key={review.authorName}>
                <article className="min-h-[22rem] border border-white/15 bg-[#16222C] p-6 sm:p-8 lg:p-10">
                  <div className="flex flex-wrap items-center gap-3">
                    <RatingStars />
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
                      Google review
                    </span>
                  </div>
                  <p className="mt-5 text-lg font-medium leading-8 text-white md:text-xl md:leading-9">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div className="mt-8 flex items-center gap-4">
                    <span className="flex h-12 w-12 items-center justify-center bg-white text-sm font-bold text-black">
                      {review.authorName
                        .split(' ')
                        .map((part) => part[0])
                        .join('')}
                    </span>
                    <span>
                      <span className="block text-base font-semibold text-white">
                        {review.authorName}
                      </span>
                      <span className="block text-sm text-gray-400">Google customer</span>
                    </span>
                  </div>
                </article>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="mt-10 text-center">
          <a
            href={googleReviewPlatform.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-white px-8 py-4 text-sm font-semibold uppercase tracking-wider text-black transition-colors hover:bg-gray-100"
          >
            Read our Google reviews
          </a>
        </div>

        <div className="mx-auto mt-10 max-w-5xl border-t border-white/15 pt-8">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.25em] text-gray-400">
            More review profiles
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {secondaryReviewPlatforms.map((platform) => (
              <a
                key={platform.name}
                href={platform.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between gap-4 border border-white/15 bg-white/5 p-5 transition-colors hover:border-white hover:bg-white hover:text-black"
                aria-label={`${platform.cta} in a new tab`}
              >
                <span>
                  <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-gray-400 group-hover:text-gray-600">
                    {platform.label}
                  </span>
                  <span className="mt-1 block text-lg font-semibold">
                    {platform.name}
                  </span>
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </a>
            ))}
          </div>
        </div>
      </div>

      <style jsx global>{`
        .okt-google-reviews-swiper {
          overflow: hidden;
          padding-bottom: ${hasMultipleReviews ? '2.75rem' : '0'};
        }

        .okt-google-reviews-swiper .swiper-button-prev,
        .okt-google-reviews-swiper .swiper-button-next {
          color: #fff;
          top: calc(50% - 1.25rem);
        }

        .okt-google-reviews-swiper .swiper-button-prev::after,
        .okt-google-reviews-swiper .swiper-button-next::after {
          font-size: 1.6rem;
          font-weight: 700;
        }

        .okt-google-reviews-swiper .swiper-pagination {
          bottom: 0 !important;
        }

        .okt-google-review-bullet {
          display: inline-block;
          width: 2rem;
          height: 0.18rem;
          margin: 0 0.3rem;
          background: rgba(255, 255, 255, 0.32);
          cursor: pointer;
          transition: background 0.2s ease, width 0.2s ease;
        }

        .okt-google-review-bullet-active {
          width: 3rem;
          background: #fff;
        }

        @media (max-width: 640px) {
          .okt-google-reviews-swiper .swiper-button-prev,
          .okt-google-reviews-swiper .swiper-button-next {
            display: none;
          }
        }
      `}</style>
    </section>
  );
}
